import type { Router } from 'vue-router';

/**
 * ITDEVELOP-3499: a tab opened before a deploy still runs the previous `index-*.js`, whose lazy
 * route chunks no longer exist on the server once the new build is up. The first navigation to an
 * unvisited route then fails with "Failed to fetch dynamically imported module" and nothing
 * happens until the user refreshes.
 *
 * The router sees that failure as a navigation error and knows where the user was going, so we
 * do a full page load of the target route: the tab picks up the current build and lands where the
 * click intended, instead of reloading the page it was already on.
 *
 * The recovery is attempted once per window: if the freshly loaded page fails the same way (the
 * index itself is stale or the deploy is mid-flight) the error is logged instead of looping.
 */
export const STALE_CHUNK_RELOAD_KEY = 'stale-chunk-reload-at';

/** How long after an automatic recovery a second chunk failure is treated as "still broken". */
export const RELOAD_LOOP_WINDOW_MS = 30_000;

// Chrome, Firefox and Safari word the failed dynamic import differently. Vite's preload helper
// loads a route's CSS before its JS, so a stale CSS file fails first, with its own wording.
const CHUNK_LOAD_ERROR =
    /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS for/i;

export function isStaleChunkError(error: unknown): boolean {
    return error instanceof Error && CHUNK_LOAD_ERROR.test(error.message);
}

type RecoveryTarget = {
    location: { assign: (url: string) => void };
    sessionStorage: Pick<Storage, 'getItem' | 'setItem'>;
};

export function installStaleChunkReload(
    routers: Pick<Router, 'onError'>[],
    target: RecoveryTarget = window,
    now: () => number = Date.now,
) {
    for (const router of routers) {
        router.onError((error, to) => {
            if (isStaleChunkError(error) && claimRecovery(target, now())) {
                target.location.assign(to.fullPath);
                return;
            }
            // vue-router only logs navigation errors itself when no onError handler is registered.
            console.error(error);
        });
    }
}

/**
 * Records a recovery at `at` and returns true, unless one is already on record inside the loop
 * window. Also returns false when sessionStorage is unavailable (the browser blocks site storage
 * and access throws): without it there is no loop guard, so the error is left to surface.
 */
function claimRecovery(target: RecoveryTarget, at: number): boolean {
    try {
        const storage = target.sessionStorage;
        const lastReloadAt = storage.getItem(STALE_CHUNK_RELOAD_KEY);
        if (lastReloadAt !== null && at - Number(lastReloadAt) < RELOAD_LOOP_WINDOW_MS) {
            // We already recovered once for this; let the error surface rather than spin.
            return false;
        }
        storage.setItem(STALE_CHUNK_RELOAD_KEY, String(at));
        return true;
    } catch {
        return false;
    }
}
